import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Radio, MapPin, Plane, ClipboardCheck, RefreshCw, Users, Search,
  ChevronRight, AlertTriangle, Clock, Loader2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";

// ---------------------------------------------------------------------------
// Leaflet default-icon fix — CRA's asset resolver mangles the bundled PNG
// paths, so we re-point Leaflet to the public CDN copies. Done once here.
// ---------------------------------------------------------------------------
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const INSIDE_COLOR = "#059669";   // emerald-600
const OUTSIDE_COLOR = "#dc2626";  // red-600
const UNKNOWN_COLOR = "#a1a1aa";  // zinc-400

function pinIcon(color) {
  // Simple inline SVG marker so we can tint by geofence status without shipping extra assets.
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
  <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
    <path fill="${color}" stroke="white" stroke-width="1.5"
      d="M14 1c-7 0-12 5.4-12 12.2 0 8.8 12 25.8 12 25.8s12-17 12-25.8C26 6.4 21 1 14 1z"/>
    <circle cx="14" cy="13" r="4.5" fill="white"/>
  </svg>`;
  return L.divIcon({
    className: "on-duty-pin",
    html: svg,
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -34],
  });
}

const INSIDE_ICON = pinIcon(INSIDE_COLOR);
const OUTSIDE_ICON = pinIcon(OUTSIDE_COLOR);
const UNKNOWN_ICON = pinIcon(UNKNOWN_COLOR);

function iconFor(status) {
  if (status === "inside") return INSIDE_ICON;
  if (status === "outside") return OUTSIDE_ICON;
  return UNKNOWN_ICON;
}

function elapsedLabel(minutes) {
  if (minutes == null) return "—";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function mstTimeShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-CA", {
      hour: "2-digit", minute: "2-digit", timeZone: "America/Denver",
    });
  } catch { return "—"; }
}

function kindLabel(kind) {
  if (kind === "duty") return "On Duty";
  if (kind === "whole_day") return "Whole Day";
  return "Clocked In";
}

function kindClass(kind) {
  if (kind === "duty") return "bg-indigo-100 text-indigo-800 border-indigo-200";
  if (kind === "whole_day") return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-emerald-100 text-emerald-800 border-emerald-200";
}

function geofenceBadge(loc) {
  const status = loc?.geofence_status;
  if (!status) return null;
  const map = {
    inside:          { label: "In-radius",  cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    outside:         { label: "Out of range", cls: "bg-red-100 text-red-800 border-red-200" },
    denied:          { label: "GPS denied", cls: "bg-red-100 text-red-800 border-red-200" },
    unsupported:     { label: "GPS n/a",    cls: "bg-red-100 text-red-800 border-red-200" },
    unavailable:     { label: "No GPS",     cls: "bg-red-100 text-red-800 border-red-200" },
    "not-configured":{ label: "No geofence", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  };
  const cfg = map[status] || { label: status, cls: "bg-gray-100 text-gray-600 border-gray-200" };
  const dist = loc?.distance_km;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest ${cfg.cls}`}
      title={dist != null ? `${dist} km from ${loc?.city_name || loc?.city_code || "site"}` : cfg.label}
    >
      <MapPin className="w-2.5 h-2.5" />
      {cfg.label}
      {dist != null && <span className="text-[9px]">· {dist}km</span>}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Auto-fit helper — recenters the map on the current pin bounds.
// ---------------------------------------------------------------------------
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 8);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function OnDutyNow() {
  const [data, setData] = useState({ as_of: null, sessions: [] });
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  // Folded-away city sections (set of group keys).
  const [collapsedCities, setCollapsedCities] = useState(() => new Set());
  const timerRef = useRef(null);

  const toggleCity = (key) =>
    setCollapsedCities((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  const load = async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: resp } = await api.get("/team/on-duty");
      setData(resp);
      setRefreshedAt(new Date());
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to load on-duty list";
      setError(msg);
      if (!silent) toast.error(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => { load({ silent: true }); }, 60_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const departments = useMemo(() => {
    const set = new Set();
    for (const s of data.sessions || []) {
      if (s.user?.department) set.add(s.user.department);
    }
    return Array.from(set).sort();
  }, [data.sessions]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data.sessions || []).filter((s) => {
      if (dept !== "all" && (s.user?.department || "") !== dept) return false;
      if (!q) return true;
      return (
        (s.user?.display_name || "").toLowerCase().includes(q) ||
        (s.user?.employee_number || "").toString().toLowerCase().includes(q) ||
        (s.user?.department || "").toLowerCase().includes(q) ||
        (s.aircraft_tail || "").toLowerCase().includes(q) ||
        (s.task_type_name || "").toLowerCase().includes(q)
      );
    });
  }, [data.sessions, query, dept]);

  // Group filtered sessions by city (code + name). Cities alphabetise by
  // display label; sessions with no city fall into an "Unassigned" bucket
  // that sinks to the bottom. Requested by the user: "Live on-duty roster,
  // group by city."
  const grouped = useMemo(() => {
    const bucketByKey = new Map();
    for (const s of filtered) {
      const code = s.location?.city_code || null;
      const name = s.location?.city_name || null;
      const key = code || "__unassigned__";
      if (!bucketByKey.has(key)) {
        bucketByKey.set(key, {
          key,
          code,
          name,
          label: code && name ? `${code} · ${name}` : code || name || "Unassigned",
          rows: [],
        });
      }
      bucketByKey.get(key).rows.push(s);
    }
    return Array.from(bucketByKey.values()).sort((a, b) => {
      if (a.key === "__unassigned__") return 1;
      if (b.key === "__unassigned__") return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filtered]);

  const mapPoints = useMemo(() => {
    return filtered
      .filter((s) => s.location?.client_latitude != null && s.location?.client_longitude != null)
      .map((s) => ({
        id: s.session_id,
        lat: s.location.client_latitude,
        lng: s.location.client_longitude,
        session: s,
      }));
  }, [filtered]);

  const siteCircles = useMemo(() => {
    const seen = new Map();
    for (const s of filtered) {
      const loc = s.location || {};
      if (loc.city_latitude != null && loc.city_longitude != null && loc.radius_km) {
        const key = loc.city_code || `${loc.city_latitude},${loc.city_longitude}`;
        if (!seen.has(key)) {
          seen.set(key, {
            key,
            lat: loc.city_latitude,
            lng: loc.city_longitude,
            radius_m: Math.max(200, Math.round(loc.radius_km * 1000)),
            name: loc.city_name || loc.city_code,
          });
        }
      }
    }
    return Array.from(seen.values());
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { total: filtered.length, clock: 0, duty: 0, whole_day: 0, out: 0 };
    for (const s of filtered) {
      if (s.kind === "duty") c.duty += 1;
      else if (s.kind === "whole_day") c.whole_day += 1;
      else c.clock += 1;
      if (s.location?.geofence_status === "outside") c.out += 1;
    }
    return c;
  }, [filtered]);

  const totalUsers = useMemo(() => new Set(filtered.map((s) => s.user?.id)).size, [filtered]);

  return (
    <section
      className="mt-10 border border-summit-hairline rounded-lg bg-white overflow-hidden"
      data-testid="on-duty-section"
    >
      <div
        className="w-full px-5 py-4 border-b border-summit-hairline flex items-center justify-between flex-wrap gap-3 hover:bg-summit-glacier-soft/60 transition-colors cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        data-testid="on-duty-toggle"
      >
        <div className="flex items-center gap-3">
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${collapsed ? "rotate-0" : "rotate-90"}`} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <Radio className="w-3 h-3 text-emerald-600" />
              Live · MST
              {refreshedAt && (
                <span className="text-gray-400 normal-case tracking-normal">
                  · updated {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
            </div>
            <h2 className="font-display text-lg font-medium tracking-tight mt-0.5" data-testid="on-duty-title">
              On duty now
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <StatChip label="Total" value={counts.total} testid="on-duty-count-total" />
          <StatChip label="Clock" value={counts.clock} tone="emerald" testid="on-duty-count-clock" />
          <StatChip label="Duty" value={counts.duty} tone="indigo" testid="on-duty-count-duty" />
          <StatChip label="Whole-Day" value={counts.whole_day} tone="amber" testid="on-duty-count-whole-day" />
          {counts.out > 0 && (
            <StatChip label="Out of range" value={counts.out} tone="red" testid="on-duty-count-out" />
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 border-summit-hairline text-summit-primary hover:bg-summit-glacier"
            onClick={() => load()}
            disabled={loading}
            data-testid="on-duty-refresh"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1.5 text-xs">Refresh</span>
          </Button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-5 py-4" data-testid="on-duty-body">
          {/* ---- Filters ---- */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, emp #, aircraft…"
                className="h-9 pl-8 pr-3 rounded-md border-summit-hairline text-xs bg-white w-64"
                data-testid="on-duty-search"
              />
            </div>
            <select
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              className="h-9 rounded-md border border-summit-hairline px-2 text-xs bg-white"
              data-testid="on-duty-dept-filter"
            >
              <option value="all">All departments</option>
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1.5">
              <Users className="w-3 h-3" /> {totalUsers} user{totalUsers === 1 ? "" : "s"}
            </div>
          </div>

          {error && (
            <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-2" data-testid="on-duty-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          {loading && filtered.length === 0 ? (
            <div className="py-10 text-center text-xs text-gray-400" data-testid="on-duty-loading">
              <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading live status…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400" data-testid="on-duty-empty">
              No one is clocked in or on duty right now.
            </div>
          ) : (
            <>
              {/* ---- Live table ---- */}
              <div className="border border-summit-hairline rounded-md overflow-x-auto" data-testid="on-duty-table-wrap">
                <table className="w-full text-sm" data-testid="on-duty-table">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-summit-hairline bg-summit-canvas/60">
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Name</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Emp #</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Dept</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Type</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Aircraft / Task</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Started (MST)</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Elapsed</th>
                      <th className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((grp) => {
                      const cityFolded = collapsedCities.has(grp.key);
                      return (
                      <React.Fragment key={`city-${grp.key}`}>
                        <tr
                          className="bg-summit-glacier/40 border-b border-summit-hairline cursor-pointer hover:bg-summit-glacier/70 transition-colors select-none"
                          onClick={() => toggleCity(grp.key)}
                          data-testid={`on-duty-city-header-${grp.key}`}
                        >
                          <td colSpan={8} className="px-3 py-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform ${cityFolded ? "rotate-0" : "rotate-90"}`} />
                              <MapPin className="w-3 h-3 text-summit-primary" />
                              <span className="font-mono text-[10px] uppercase tracking-widest text-summit-ink">
                                {grp.label}
                              </span>
                              <span
                                className="ml-auto font-mono text-[10px] uppercase tracking-widest text-gray-500"
                                data-testid={`on-duty-city-count-${grp.key}`}
                              >
                                {grp.rows.length} on duty
                              </span>
                            </div>
                          </td>
                        </tr>
                        {!cityFolded && grp.rows.map((s) => {
                          const loc = s.location || {};
                          const hasFix = loc.client_latitude != null && loc.client_longitude != null;
                          const lastFixIso = s.last_ping_at || s.started_at;
                          const ageMin = lastFixIso ? Math.max(0, Math.round((Date.now() - new Date(lastFixIso).getTime()) / 60000)) : null;
                          const stale = ageMin != null && ageMin > 45;
                          return (
                          <React.Fragment key={s.session_id}>
                          <tr
                            className="hover:bg-summit-glacier-soft/40"
                            data-testid={`on-duty-row-${s.user?.employee_number || s.user?.id}`}
                          >
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-sm font-medium">{s.user?.display_name}</div>
                          {s.user?.job_title && (
                            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{s.user.job_title}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{s.user?.employee_number || "—"}</td>
                        <td className="px-3 py-2 text-xs">{s.user?.department || "—"}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest ${kindClass(s.kind)}`}
                            data-testid={`on-duty-kind-${s.session_id}`}
                          >
                            {kindLabel(s.kind)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {s.aircraft_tail ? (
                            <span className="inline-flex items-center gap-1">
                              <Plane className="w-3 h-3 text-summit-primary" />
                              <span className="font-mono">{s.aircraft_tail}</span>
                              {s.task_type_name && <span className="text-gray-500">· {s.task_type_name}</span>}
                              {s.is_ot && <span className="ml-1 px-1 rounded bg-orange-100 text-orange-800 font-mono text-[9px] uppercase tracking-widest">OT</span>}
                              {s.is_capex && <span className="ml-1 px-1 rounded bg-purple-100 text-purple-800 font-mono text-[9px] uppercase tracking-widest">CAPEX</span>}
                            </span>
                          ) : s.kind === "duty" ? (
                            <span className="inline-flex items-center gap-1 text-gray-500">
                              <ClipboardCheck className="w-3 h-3" /> Duty (no clock)
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{mstTimeShort(s.started_at)}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3 text-gray-400" />
                            {elapsedLabel(s.elapsed_minutes)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          {geofenceBadge(s.location)}
                        </td>
                      </tr>
                          {/* Sub-line: last known location from the most recent
                              30-min ping (falls back to the start-duty fix).
                              Amber when the fix is older than 45 minutes. */}
                          <tr
                            className="border-b border-summit-hairline last:border-0"
                            data-testid={`on-duty-lastloc-${s.session_id}`}
                          >
                            <td colSpan={8} className="px-3 pb-1.5 pt-0">
                              <div className={`pl-1 flex items-center gap-1.5 font-mono text-[10px] ${stale ? "text-amber-700" : "text-gray-500"}`}>
                                {stale
                                  ? <AlertTriangle className="w-2.5 h-2.5 text-amber-600 shrink-0" />
                                  : <MapPin className="w-2.5 h-2.5 text-gray-400 shrink-0" />}
                                <span className={`uppercase tracking-widest ${stale ? "text-amber-600" : "text-gray-400"}`}>Last known location</span>
                                <span>
                                  {loc.distance_km != null
                                    ? `${loc.distance_km.toFixed(loc.distance_km >= 10 ? 1 : 2)} km from ${grp.label.split(" · ")[1] || grp.label}`
                                    : hasFix
                                      ? `${loc.client_latitude.toFixed(4)}, ${loc.client_longitude.toFixed(4)}`
                                      : "no GPS fix"}
                                </span>
                                <span className={stale ? "text-amber-600" : "text-gray-400"}>
                                  · {s.last_ping_at
                                    ? `pinged ${mstTimeShort(s.last_ping_at)} MST`
                                    : `at start ${mstTimeShort(s.started_at)} MST`}
                                  {stale && ageMin != null && (
                                    <span className="font-semibold" data-testid={`on-duty-stale-${s.session_id}`}>
                                      {" "}· stale ({elapsedLabel(ageMin)} ago)
                                    </span>
                                  )}
                                </span>
                              </div>
                            </td>
                          </tr>
                          </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ---- Map ---- */}
              <div className="mt-5 border border-summit-hairline rounded-md overflow-hidden" data-testid="on-duty-map-wrap">
                <button
                  type="button"
                  onClick={() => setMapCollapsed((c) => !c)}
                  className="w-full px-4 py-2.5 border-b border-summit-hairline flex items-center justify-between bg-summit-canvas hover:bg-summit-glacier-soft/60 text-left"
                  data-testid="on-duty-map-toggle"
                  aria-expanded={!mapCollapsed}
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${mapCollapsed ? "rotate-0" : "rotate-90"}`} />
                    <MapPin className="w-3.5 h-3.5 text-summit-primary" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-600">
                      Map · {mapPoints.length} pin{mapPoints.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                    OpenStreetMap
                  </span>
                </button>
                {!mapCollapsed && (
                  <div style={{ height: 380 }} data-testid="on-duty-map">
                    {mapPoints.length === 0 ? (
                      <div className="h-full grid place-items-center text-xs text-gray-400 bg-summit-glacier-soft/30">
                        No GPS coordinates available for the current filter.
                      </div>
                    ) : (
                      <MapContainer
                        center={[mapPoints[0].lat, mapPoints[0].lng]}
                        zoom={8}
                        scrollWheelZoom={false}
                        style={{ height: "100%", width: "100%" }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        />
                        {siteCircles.map((c) => (
                          <Circle
                            key={`geo-${c.key}`}
                            center={[c.lat, c.lng]}
                            radius={c.radius_m}
                            pathOptions={{
                              color: "#0284c7", weight: 1.5,
                              fillColor: "#0ea5e9", fillOpacity: 0.08,
                            }}
                          >
                            <Popup>
                              <div className="text-xs">
                                <div className="font-medium">{c.name}</div>
                                <div className="text-gray-500">Radius: {(c.radius_m / 1000).toFixed(1)} km</div>
                              </div>
                            </Popup>
                          </Circle>
                        ))}
                        {mapPoints.map((p) => {
                          const s = p.session;
                          const loc = s.location || {};
                          return (
                            <Marker
                              key={p.id}
                              position={[p.lat, p.lng]}
                              icon={iconFor(loc.geofence_status)}
                            >
                              <Popup>
                                <div className="text-xs space-y-1 min-w-[180px]">
                                  <div className="font-semibold text-sm">{s.user?.display_name}</div>
                                  <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                                    #{s.user?.employee_number || "—"} · {s.user?.department || "—"}
                                  </div>
                                  <div className="mt-1">
                                    <span
                                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest ${kindClass(s.kind)}`}
                                    >
                                      {kindLabel(s.kind)}
                                    </span>
                                  </div>
                                  {s.aircraft_tail && (
                                    <div className="text-xs">
                                      <span className="font-mono">{s.aircraft_tail}</span>
                                      {s.task_type_name && <span className="text-gray-500"> · {s.task_type_name}</span>}
                                    </div>
                                  )}
                                  <div className="text-[11px] text-gray-600">
                                    Started {mstTimeShort(s.started_at)} · {elapsedLabel(s.elapsed_minutes)} elapsed
                                  </div>
                                  <div>{geofenceBadge(loc)}</div>
                                  {loc.city_name && (
                                    <div className="font-mono text-[10px] text-gray-500">
                                      Site: {loc.city_name}
                                      {loc.distance_km != null && <> · {loc.distance_km} km away</>}
                                    </div>
                                  )}
                                </div>
                              </Popup>
                            </Marker>
                          );
                        })}
                        <FitBounds points={mapPoints} />
                      </MapContainer>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function StatChip({ label, value, tone, testid }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  const cls = toneMap[tone] || "bg-summit-glacier text-summit-ink border-summit-hairline";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border font-mono text-[10px] uppercase tracking-widest ${cls}`}
      data-testid={testid}
    >
      {label}
      <span className="tabular-nums font-semibold">{value}</span>
    </span>
  );
}
